import React, { Component } from 'react'
import { Route, RouteComponentProps, Switch } from 'react-router-dom'
import { AxiosInstance } from 'axios'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Meta, getLocalizedLabel } from '../common'
import { Button, Checkbox, Container, Dimmer, Dropdown, Grid, Header, Label, Loader, Segment } from 'semantic-ui-react'
import FileSaver from 'file-saver'
import FileSelect from './FileSelect'
import './UserScript.css'

interface UserScriptProps extends WithTranslation, RouteComponentProps {
    meta: Meta,
    http: AxiosInstance,
    type: string,
    query: any,
    params: any,
    close: () => void
}

interface UserScriptState {
    running: boolean,
    testRun: boolean,
    runUnproven: boolean,
    profile: string,
    infiletype: string,
    outfiletype: string,
    outScreenOnly: boolean,
    screenOutput: (React.JSX.Element|string)[],
    txtOutput: string[],
    statCounts: any,
}

class UserScript extends Component<UserScriptProps, UserScriptState> {

    state = {
        running: false,
        testRun: false,
        runUnproven: false,
        profile: '',
        infiletype: '',
        outfiletype: '',
        outScreenOnly: true,
        txtOutput: [],
        screenOutput: [],
        statCounts: {ERROR: 0, FAILURE: 0, WARNING: 0, OK: 0, PASSED: 0}
    }

    async execute(test: boolean) {
        const { type, meta, query, params, http } = this.props
        const { infiletype, outfiletype, profile, outScreenOnly } = this.state
        const smeta = meta.resources['user_bin'][type]
        let req = {}
        if (test) req['test'] = true
        if (profile) req['profile'] = profile
        if (infiletype) req['input'] = infiletype
        if (outfiletype && !outScreenOnly) req['output'] = outfiletype
        if (smeta.params?.type==='export') req['query'] = query
        if (params) req['params'] = params
        const path = type.replaceAll(':', '/').replace(/^\//,'_\/')

        this.setState({running: true, testRun: test, txtOutput: [], screenOutput: [], statCounts: {ERROR: 0, FAILURE: 0, WARNING: 0, OK: 0, PASSED: 0}})
        let stats = {ERROR: 0, FAILURE: 0, WARNING: 0, OK: 0, PASSED: 0}

        // streaming not supported by axios in browser!
        /*const response = await http.post('scripts/'+path, req, {
            headers: {}, 
            responseType: 'stream'
        })
        
        const stream = response.data
        
        stream.on('data', data => {console.log(data)
            this.setState({output: output+data})
        })

        stream.on('end', () => {
            this.setState({running: false})
            console.log("stream done")
        })*/

        const response = await fetch(http.defaults.baseURL+'scripts/'+path, {
            method: "POST", // *GET, POST, PUT, DELETE, etc.
            mode: "cors", // no-cors, *cors, same-origin
            cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
            credentials: "same-origin", // include, *same-origin, omit
            headers: {
              "Content-Type": "application/json"
            },
            redirect: "follow", // manual, *follow, error
            referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
            body: JSON.stringify(req), // body data type must match "Content-Type" header
          
        })

        if (!response || !response.body) {
            console.log("NO RESPONSE", response)
            this.setState({running: false, testRun: false})
            return
        }
            
        const reader = response.body.getReader()

        const highlights = [
            [new RegExp("(?<=\\[)(PASSED)(?=\\])", 'gi'), 'PASSED', 'success'],
            [new RegExp("(?<=\\[)(OK)(?=\\])", 'gi'), 'OK', 'success'],
            [new RegExp("(ERROR)", 'gi'), 'ERROR', 'error'],
            [new RegExp("(FAILURE)", 'gi'), 'FAILURE', 'error'],
            [new RegExp("(WARNING)", 'gi'), 'WARNING', 'warning']
        ]
            
        while (true) {
            const { done, value } = await reader.read()
            
            if (done) {
                this.setState({running: false})
                break
            }
            
            let data = new TextDecoder().decode(value)
            let comps: (React.JSX.Element|string)[] = [data]
            for (let h of highlights) {
                const regex = h[0] as RegExp
                const contents = h[1] as string
                const spanClass = h[2] as string
                comps = comps.reduce((acc, comp) => {
                    if (typeof comp === 'string') {
                        const parts = comp.split(regex)
                        const newComps = parts.map(part => {
                            /*regex.test(part)*/ 
                            if (part===contents) {
                                stats[contents] += 1
                                return (<span className={spanClass}>{part}</span>)
                            } else {
                                return(part)
                            }
                        })
                        acc = [...acc, ...newComps]
                    } else {
                        acc.push(comp)
                    }
                    return acc
                }, [] as (React.JSX.Element|string)[])
            }
            this.setState(prev => ({screenOutput: [...prev.screenOutput, ...comps], txtOutput: [...prev.txtOutput, data], statCounts: stats}))
        }
    }

    save() {
        const { meta, type } = this.props
        const { screenOutput, txtOutput } = this.state
        const smeta = meta.resources['user_bin'][type]
        const fileName = smeta.params?.default_filename || smeta.params?.type==='export' ? 'export.json' : 'log.txt'

        const fileType = 'text/plain;charset=UTF-8;'
        const rdata = new Blob([txtOutput.join('')], {type: fileType})
        FileSaver.saveAs(rdata, fileName)
}

    render() {
        const { i18n, t, meta, type, close } = this.props
        const { screenOutput, running, testRun, infiletype, outfiletype, profile, outScreenOnly, statCounts, runUnproven } = this.state
        const smeta = meta.resources['user_bin'][type]

        if (!smeta) return(<>{type}</>)

        if (!profile && smeta.params?.profiles) {
            this.setState({profile: smeta.params.profiles[0].name})
        }

        const profileOptions = smeta.params?.profiles ? smeta.params.profiles.map(p => ({key: p.name, value: p.name, text: getLocalizedLabel(p.label, i18n.language)})) : []

        /*const toggleScreenOnly = () => {
            if (this.state.outScreenOnly)
                this.setState({outScreenOnly: false})
        }*/

        const unprovenImport = (
            (smeta.params?.type==='import' || smeta.params?.type==='update') && 
            ((!statCounts['PASSED'] && !statCounts['WARNING'])|| statCounts['ERROR'] || statCounts['FAILURE'])
        )

        return(
            <Container>
            <Segment color="grey">
            <Grid>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Header as="h1">{getLocalizedLabel(smeta.label, i18n.language)}</Header>
                    </Grid.Column>
                </Grid.Row>
                { profileOptions.length ?
                <>
                <Grid.Row verticalAlign='middle'>
                    <Grid.Column width={2}>
                        <div className='label'>{t('Profile')}</div>
                    </Grid.Column>
                    <Grid.Column width={14}>
                        <Dropdown fluid selection value={profile} options={profileOptions} onChange={(e, {value}) => this.setState({profile: value as string})}/>
                    </Grid.Column>
                </Grid.Row>
                </>
                : null}
                { smeta.params?.path_in ?
                <>
                <Grid.Row className='condensed'>
                    <Grid.Column width={16}>
                        <Header as="h3">{t('Input')}</Header>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <FileSelect {...this.props} rootType={smeta.params.path_in} setSelected={(type) => this.setState({infiletype: type})}/>
                    </Grid.Column>
                </Grid.Row>
                </>
                : null}
                { smeta.params?.path_out ?
                <>
                    <Grid.Row className='condensed'>
                        <Grid.Column width={16}>
                            <Header as="h3">{t('Output')}</Header>
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row className='condensed'>
                        <Grid.Column width={16}>
                            <Checkbox checked={outScreenOnly} onClick={()=>this.setState({outScreenOnly: !this.state.outScreenOnly})}/> {t('only on screen')}
                        </Grid.Column>
                    </Grid.Row>
                    { outScreenOnly ? null :
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <FileSelect {...this.props} rootType={smeta.params.path_out} setSelected={(type) => this.setState({outfiletype: type})} output={true}/>
                        </Grid.Column>
                    </Grid.Row>
                    }
                </>
                : null}
                <Grid.Row><Grid.Column width={16}></Grid.Column></Grid.Row>
                <Grid.Row>
                    <Grid.Column width={12}>
                        { smeta.params?.type==='import' || smeta.params?.type==='update' ? 
                            <Button button color="yellow" content={t('Test')} onClick={()=>this.execute(true)} loading={running && testRun}
                            disabled={running || (smeta.params?.path_in && !infiletype)}/> 
                        : null}
                        <Button button color="orange" content={t('Run')} onClick={()=>this.execute(false)} loading={running && !testRun}
                            disabled={running || (smeta.params?.path_in && !infiletype) || (unprovenImport && !runUnproven)}/>
                        {unprovenImport ? 
                            <>
                            <Checkbox checked={runUnproven} onClick={() => this.setState({runUnproven: !runUnproven})}
                                label={t("I want to run this despite possible issues")}/>
                            </>
                        : null}
                    </Grid.Column>
                    <Grid.Column width={4} textAlign='right'>
                        <Button button color="grey" content={t('Close')} onClick={()=>close()}/>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            </Segment>
            { running || screenOutput.length ?
            <Grid className='compact'>
                <Grid.Row>
                    <Grid.Column width={4}>
                    <Header as="h2">{t('Output')}</Header>
                    </Grid.Column>
                    <Grid.Column width={8}>
                        <>(
                            <span className='count'>{t('Error')}: <span className={statCounts['ERROR'] ? 'error':''}>{statCounts['ERROR']}</span></span>
                            <span className='count'>{t('Failure')}: <span className={statCounts['FAILURE'] ? 'error':''}>{statCounts['FAILURE']}</span></span>
                            <span className='count'>{t('Warning')}: <span className={statCounts['WARNING'] ? 'warning':''}>{statCounts['WARNING']}</span></span>
                            <span className='count'>{t('Passed')}: <span className={statCounts['PASSED'] ? 'success':''}>{statCounts['PASSED']}</span></span>
                            <span className='count'>{t('OK')}: <span className={statCounts['OK'] ? 'success':''}>{statCounts['OK']}</span></span>
                        )</>
                    </Grid.Column>
                    <Grid.Column width={4} textAlign='right'>
                        <Button button icon="download" color="blue" title={t('Download')} onClick={()=>this.save()} disabled={running} size="tiny"/>
                    </Grid.Column>
                </Grid.Row>
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment>
                            <pre className='wrap'>{ screenOutput }</pre>
                            {running ?
                            <div>
                                <Loader active inline/>
                            </div>
                            : null }
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            : null}
            </Container>
        )
    }
}

export default withTranslation()(UserScript)